import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiCheck, FiCreditCard } from 'react-icons/fi';
import membershipService from '../services/membership.service';

const MembershipPlans = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { memberId } = location.state || {};
  const hasFetched = useRef(false);

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlanType, setSelectedPlanType] = useState('all');

  useEffect(() => {
    // Prevent duplicate fetches on mount
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await membershipService.getAllPlans({ is_active: 'true' });
      setPlans(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch membership plans');
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPlans = selectedPlanType === 'all'
    ? plans
    : plans.filter(plan => plan.plan_type === selectedPlanType);

  const getPlanTypeLabel = (type) => {
    const labels = {
      monthly: 'Monthly',
      quarterly: 'Quarterly (3 Months)',
      half_yearly: 'Half Yearly (6 Months)',
      annual: 'Annual (12 Months)',
      custom: 'Custom'
    };
    return labels[type] || type;
  };

  const handlePurchase = (plan) => {
    navigate('/membership/purchase', { state: { plan, memberId } });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Membership Plans</h1>
        <p className="text-gray-600 mt-1">Choose the perfect plan for your fitness journey</p>
        {memberId && (
          <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              Selecting a plan for member. After selecting a plan, the member will be pre-selected in the purchase flow.
            </p>
          </div>
        )}
      </div>

      {/* Plan Type Filter */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedPlanType('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedPlanType === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All Plans
          </button>
          <button
            onClick={() => setSelectedPlanType('monthly')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedPlanType === 'monthly'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setSelectedPlanType('quarterly')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedPlanType === 'quarterly'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Quarterly
          </button>
          <button
            onClick={() => setSelectedPlanType('half_yearly')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedPlanType === 'half_yearly'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Half Yearly
          </button>
          <button
            onClick={() => setSelectedPlanType('annual')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedPlanType === 'annual'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Annual
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      {filteredPlans.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">No membership plans available</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPlans.map((plan) => (
            <div
              key={plan.id}
              className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
            >
              {/* Plan Header */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 text-white">
                <h3 className="text-2xl font-bold">{plan.name}</h3>
                <p className="text-blue-100 mt-1">{getPlanTypeLabel(plan.plan_type)}</p>
                <div className="mt-4">
                  <div className="flex items-center space-x-2">
                    <span className={`text-lg ${plan.discount_percent > 0 ? 'text-blue-200 line-through' : 'text-blue-100'}`}>
                      ₹{parseFloat(plan.price).toFixed(2)}
                    </span>
                    {plan.discount_percent > 0 ? (
                      <span className="bg-yellow-400 text-blue-900 px-2 py-1 rounded text-sm font-semibold">
                        {plan.discount_percent}% OFF
                      </span>
                    ) : (
                      <span className="bg-blue-400 text-white px-2 py-1 rounded text-sm font-semibold">
                        0% OFF
                      </span>
                    )}
                  </div>
                  <div className="text-4xl font-bold mt-2">
                    ₹{parseFloat(plan.final_price).toFixed(2)}
                  </div>
                  <p className="text-blue-100 text-sm mt-1">
                    for {plan.duration_days} days
                  </p>
                </div>
              </div>

              {/* Plan Body */}
              <div className="p-6">
                {plan.description && (
                  <p className="text-gray-600 mb-4">{plan.description}</p>
                )}

                {/* Features */}
                {plan.features && plan.features.length > 0 && (
                  <div className="space-y-3 mb-6">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-start">
                        <FiCheck className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Purchase Button */}
                <button
                  onClick={() => handlePurchase(plan)}
                  className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  <FiCreditCard className="mr-2" />
                  Purchase Plan
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-2">Payment Methods</h3>
        <p className="text-blue-800 text-sm">
          We accept payments via UPI, Credit/Debit Cards, Net Banking, and Wallets through Razorpay secure payment gateway.
        </p>
      </div>
    </div>
  );
};

export default MembershipPlans;
